// utils/reactNativebridge.js
class ReactNativeBridge {
  // 내부 상태 (앱 정보 캐시)
  static appInfo = null;
  static listenerInitialized = false;
  static MIN_AD_SDK_VERSION = { ios: '1.4.0', android: '100.4.0', default: '1.4.0' };

  // 초기 메시지 리스너 설정 (한 번만)
  static setupMessageListener() {
    if (this.listenerInitialized) return;

    const handleMessage = (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data && typeof data === 'object') {
          // RN에서 앱 정보 전달
          if (data.type === 'APP_INFO' || data.type === 'APP_VERSION') {
            // 기대 형태: { type: 'APP_INFO', version: 'x.y.z', platform: 'ios'|'android' }
            if (!this.appInfo) this.appInfo = {};
            if (data.version) this.appInfo.version = String(data.version);
            if (data.platform) this.appInfo.platform = String(data.platform);
            try {
              // 세션 스토리지에 보관 (세션 범위)
              if (this.appInfo.version) sessionStorage.setItem('rn_app_version', this.appInfo.version);
              if (this.appInfo.platform) sessionStorage.setItem('rn_app_platform', this.appInfo.platform);
            } catch (_) {}
          }
        }
      } catch (_) {
        // 무시
      }
    };

    // Android RN WebView는 document로 메시지를 디스패치하는 경우가 있음
    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage);

    this.listenerInitialized = true;
  }

  // React Native 앱에 메시지 전송
  static sendMessage(data) {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(data));
      return true;
    } else {
      return false;
    }
  }

  // 로그인 성공 시
  static sendLoginSuccess(token, userInfo = {}) {
    return this.sendMessage({
      type: 'LOGIN_SUCCESS',
      token: token,
      userInfo: userInfo
    });
  }

  // 로그인 실패 시
  static sendLoginError(message) {
    return this.sendMessage({
      type: 'LOGIN_ERROR',
      message: message
    });
  }

  // React Native 환경인지 확인
  static isReactNativeApp() {
    return !!window.ReactNativeWebView;
  }

  // 앱 정보 요청 (RN이 지원하는 경우)
  static async requestAppInfo(timeoutMs = 800) {
    this.setupMessageListener();
    if (!this.isReactNativeApp()) return null;

    // 이미 캐시되어 있으면 반환
    const cached = this.getAppInfoSync();
    if (cached) return cached;

    // RN에 앱 정보 요청
    this.sendMessage({ type: 'APP_INFO_REQUEST' });

    // 응답 대기 (타임아웃 시 null)
    return new Promise((resolve) => {
      let resolved = false;
      const timeout = setTimeout(() => {
        resolved = true;
        resolve(this.getAppInfoSync());
      }, timeoutMs);

      const handler = (event) => {
        if (resolved) return;
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (data && (data.type === 'APP_INFO' || data.type === 'APP_VERSION')) {
            clearTimeout(timeout);
            resolved = true;
            window.removeEventListener('message', handler);
            resolve(this.getAppInfoSync());
          }
        } catch (_) {
          // ignore
        }
      };
      window.addEventListener('message', handler);
    });
  }

  // 동기 앱 정보 조회 (캐시/스토리지 기반)
  static getAppInfoSync() {
    if (this.appInfo && (this.appInfo.version || this.appInfo.platform)) return this.appInfo;
    try {
      const version = sessionStorage.getItem('rn_app_version') || localStorage.getItem('rn_app_version');
      const platform = sessionStorage.getItem('rn_app_platform') || localStorage.getItem('rn_app_platform');
      if (version || platform) {
        this.appInfo = { version: version || undefined, platform: platform || undefined };
        return this.appInfo;
      }
    } catch (_) {}
    return null;
  }

  // 동기 앱 버전 문자열 반환
  static getAppVersionSync() {
    const info = this.getAppInfoSync();
    return info?.version || null;
  }

  // 시맨틱 버전 비교 (a vs b): 1 (a>b), 0 (==), -1 (a<b)
  static compareSemver(a, b) {
    const pa = String(a || '0.0.0').split('.').map((x) => parseInt(x, 10) || 0);
    const pb = String(b || '0.0.0').split('.').map((x) => parseInt(x, 10) || 0);
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i++) {
      const na = pa[i] || 0;
      const nb = pb[i] || 0;
      if (na > nb) return 1;
      if (na < nb) return -1;
    }
    return 0;
  }

  // 현재 앱이 광고 SDK를 지원하는지 여부 (RN이 아닐 경우 true)
  static isAdSupported(options = {}) {
    if (!this.isReactNativeApp()) return true; // 웹에서는 제한 없음
    const info = this.getAppInfoSync();
    const platform = info?.platform?.toLowerCase?.();
    const appVersion = info?.version;
    // RN인데 버전을 아직 모르면, 호환성을 위해 일단 허용 (RN 가이드 적용 전까지 차단하지 않음)
    if (!appVersion) return true;
    const minByPlatform = options.minVersionByPlatform || ReactNativeBridge.MIN_AD_SDK_VERSION;
    const minVersion = platform && minByPlatform[platform]
      ? minByPlatform[platform]
      : (minByPlatform.default || '1.4.0');
    return this.compareSemver(appVersion, minVersion) >= 0;
  }

  static getMinAdVersionForCurrentPlatform() {
    const info = this.getAppInfoSync();
    const platform = info?.platform?.toLowerCase?.();
    const minByPlatform = ReactNativeBridge.MIN_AD_SDK_VERSION;
    return platform && minByPlatform[platform]
      ? minByPlatform[platform]
      : (minByPlatform.default || '1.4.0');
  }
}

// 즉시 리스너 설정
ReactNativeBridge.setupMessageListener();

export default ReactNativeBridge; 